
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { Product } from '../types';

interface Message {
  role: 'user' | 'bot';
  text: string;
}

interface ChannelTalkProps {
  products: Product[];
  currentUser: any;
}

const ChannelTalk: React.FC<ChannelTalkProps> = ({ products, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'bot', text: '안녕하세요! 메이커스토리 AI 상담원입니다. 상품 문의나 배송에 대해 궁금한 점이 있으신가요? 반품/교환 관련 문의는 아래 상담원과 카톡하기를 이용부탁드립니다.' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setInput('');
    setIsTyping(true);

    try {
      // @google/genai coding guidelines: Create a new instance right before use.
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const productContext = products.slice(0, 10).map(p => `${p.name} (${p.price}원)`).join(', ');
      
      const response: GenerateContentResponse = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: userMessage,
        config: {
          systemInstruction: `당신은 DIY 메이커 키트 쇼핑몰 '메이커스토리'의 AI 어드바이저입니다.
          현재 판매 상품: ${productContext}.
          회원: ${currentUser?.name || '방문자'}.
          
          답변 규칙:
          1. 2~3문장 이내로 친절하게 답변하세요.
          2. 복잡한 수리나 환불 규정, 개인 결제 관련 문의는 하단의 '카카오톡 상담하기' 버튼을 이용하도록 안내하세요.
          3. 아두이노, 센서, 로봇 등에 대한 전문 지식을 바탕으로 흥미롭게 답변하세요.`,
        },
      });

      // @google/genai coding guidelines: Access .text property directly.
      const botText = response.text || '죄송합니다. 메시지 처리 중 오류가 발생했습니다. 하단의 카카오톡 상담하기를 이용해주세요.';
      setMessages(prev => [...prev, { role: 'bot', text: botText }]);
    } catch (error) {
      console.error('Gemini API Error:', error);
      setMessages(prev => [...prev, { role: 'bot', text: '지금은 AI 상담이 어렵습니다. 긴급한 문의는 카카오톡으로 부탁드립니다.' }]);
    } finally {
      setIsTyping(false);
    }
  };

  const openKakaoTalk = () => {
    // 실제 카카오톡 채널 링크로 대체 가능
    window.open('https://pf.kakao.com/_juYQn', '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end">
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 w-[360px] h-[580px] bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="p-5 bg-indigo-600 text-white flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                  <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383L12 21.75l-3.676-4.637a48.974 48.974 0 01-3.476-.383C2.87 16.442 1.5 14.71 1.5 12.764v-6.02c0-1.946 1.37-3.678 3.348-3.97z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-black text-sm">메이커봇 AI</h3>
                <p className="text-[10px] opacity-80">보통 즉시 답변함</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-black/10 p-2 rounded-full transition">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3.5 rounded-2xl text-[13px] leading-relaxed shadow-sm ${
                  msg.role === 'user' 
                  ? 'bg-indigo-600 text-white rounded-tr-none' 
                  : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 p-3.5 rounded-2xl rounded-tl-none shadow-sm flex gap-1">
                  <span className="w-1 h-1 bg-indigo-300 rounded-full animate-bounce"></span>
                  <span className="w-1 h-1 bg-indigo-300 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1 h-1 bg-indigo-300 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                </div>
              </div>
            )}
          </div>

          {/* Kakao Talk Integration Area */}
          <div className="px-4 py-2 border-t border-gray-100 bg-white">
            <button 
              onClick={openKakaoTalk}
              className="w-full py-2.5 bg-[#FEE500] text-[#191919] rounded-xl text-xs font-black flex items-center justify-center gap-2 hover:brightness-95 transition active:scale-[0.98]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M12 3c-4.97 0-9 3.185-9 7.115 0 2.558 1.712 4.8 4.312 6.095l-1.09 3.993c-.056.206.19.378.358.26l4.734-3.136c.224.015.451.023.686.023 4.97 0 9-3.185 9-7.115S16.97 3 12 3z" />
              </svg>
              상담원과 카톡하기
            </button>
          </div>

          {/* Input Area */}
          <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-100 flex gap-2">
            <input 
              type="text" 
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="AI에게 질문해보세요..."
              className="flex-1 px-4 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-none"
            />
            <button 
              type="submit"
              disabled={!input.trim() || isTyping}
              className="p-2.5 bg-indigo-600 text-white rounded-xl disabled:opacity-50 hover:bg-indigo-700 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 ${
          isOpen ? 'bg-gray-900 rotate-90' : 'bg-indigo-600 animate-bounce'
        }`}
        style={{ animationDuration: '3s' }}
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="white" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        ) : (
          <div className="relative">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-7 h-7">
              <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383L12 21.75l-3.676-4.637a48.974 48.974 0 01-3.476-.383C2.87 16.442 1.5 14.71 1.5 12.764v-6.02c0-1.946 1.37-3.678 3.348-3.97zM9.75 8.25a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-4.5zm0 3a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-4.5z" clipRule="evenodd" />
            </svg>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 border-2 border-indigo-600 rounded-full"></span>
          </div>
        )}
      </button>
    </div>
  );
};

export default ChannelTalk;
